import 'rxjs/add/observable/of';
import 'rxjs/add/observable/fromPromise';
import 'rxjs/add/operator/throttleTime';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import isNil from 'lodash/isNil';
import type ApolloClient from 'apollo-client';
import { Observable } from 'rxjs/Observable';
import uuid from 'uuid';
import { assignToMe } from '@atlassian/jira-software-keyboard-shortcuts/src/controllers/assign-to-me/main.tsx';
import { scheduleAfterRender } from '@atlassian/jira-software-react-scheduler/src/index.tsx';
import { assignToMeInteraction } from '../../services/utils/performance-analytics/index.tsx';
import {
	ASSIGN_TO_ME_REQUEST,
	ASSIGN_TO_ME_REQUEST_THROTTLED,
	assignToMeRequest,
	assignToMeSuccess,
	assignToMeFailure,
	type AssignToMeFailureAction,
	type AssignToMeRequestAction,
	type AssignToMeRequestThrottledAction,
	type AssignToMeSuccessAction,
} from '../../state/actions/cards/assign-to-me/index.tsx';
import type { ActionsObservable } from '../../state/types.tsx';

type OutputAction = AssignToMeSuccessAction | AssignToMeFailureAction;

type Hooks = {
	onStart?: (action: AssignToMeRequestAction) => void;
	onSuccess?: (action: AssignToMeRequestAction) => void;
};

export const handleAssignToMeRequest = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	swagClient: ApolloClient<any>,
	action: AssignToMeRequestAction,
	{ onStart, onSuccess }: Hooks = {},
): Observable<OutputAction> => {
	const { id: optimisticId } = action.meta.optimistic;
	const { cardId, assignee, concurrentId } = action.payload;

	if (onStart) {
		onStart(action);
	}

	return Observable.fromPromise(
		assignToMe(swagClient, { issueId: `${cardId}`, accountId: assignee?.id }),
	)
		.map(() => {
			if (onSuccess) {
				onSuccess(action);
			}

			return assignToMeSuccess(optimisticId, concurrentId);
		})
		.catch(() => Observable.of(assignToMeFailure(optimisticId, concurrentId)));
};

const hooks: Hooks = {
	onStart: ({ payload: { concurrentId, assignee, fireShortcutAnalyticsFn } }) => {
		if (!isNil(concurrentId)) {
			scheduleAfterRender(() => {
				assignToMeInteraction(concurrentId).mark('feedback');
			});
		}

		if (fireShortcutAnalyticsFn) {
			fireShortcutAnalyticsFn({
				id: 'assignToMeKeyboardShortcut',
				key: 'i',
				attributes: {
					assignStatus: assignee !== null ? 'assign' : 'unassign',
				},
			});
		}
	},
	onSuccess: ({ payload: { concurrentId } }) => {
		if (!isNil(concurrentId)) {
			scheduleAfterRender(() => {
				assignToMeInteraction(concurrentId).stop();
			});
		}
	},
};

export const buildAssignToMeEpic =
	(
		swagClient: ApolloClient<any>, // eslint-disable-line @typescript-eslint/no-explicit-any
	) =>
	(action$: ActionsObservable): Observable<OutputAction> =>
		action$
			.ofType(ASSIGN_TO_ME_REQUEST)
			.flatMap((action) => handleAssignToMeRequest(swagClient, action, hooks));

export const cardAssignToMeThrottledEpic = (action$: ActionsObservable) =>
	action$
		.ofType(ASSIGN_TO_ME_REQUEST_THROTTLED)
		.throttleTime(500)
		.flatMap((action: AssignToMeRequestThrottledAction) => {
			const { cardId, assignee, fireShortcutAnalyticsFn } = action.payload;
			const concurrentId = uuid();

			assignToMeInteraction(concurrentId).start();
			return Observable.of(
				assignToMeRequest({
					cardId,
					assignee,
					concurrentId,
					fireShortcutAnalyticsFn,
				}),
			);
		});
